
import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Star } from 'lucide-react';
import { Button } from './ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Textarea } from './ui/textarea';
import { Review } from '@/hooks/useReviews';
import { useTranslation } from 'react-i18next';

const formSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5),
  description: z.string().min(10, "Review must be at least 10 characters"),
});

type FormValues = z.infer<typeof formSchema>;

interface ReviewFormProps {
  businessId: string;
  existingReview?: Review | null;
  onSubmit: (values: FormValues) => void;
  isSubmitting: boolean;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ 
  businessId, 
  existingReview, 
  onSubmit,
  isSubmitting 
}) => {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rating: existingReview?.rating || 0,
      description: existingReview?.description || "",
    },
  });

  const [hoverRating, setHoverRating] = React.useState(0);
  
  const handleSubmit = (values: FormValues) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="rating"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('service.details.rating')}</FormLabel>
              <FormControl>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => field.onChange(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= (hoverRating || field.value)
                            ? "text-yellow-500 fill-current"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </FormControl>
              <FormDescription>
                {t('service.details.rate_experience')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('service.details.your_review')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('service.details.share_experience')}
                  className="resize-none h-24"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t('service.details.review_help')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          disabled={isSubmitting} 
          className="w-full"
          isLoading={isSubmitting}
        >
          {existingReview ? t('service.details.update_review') : t('service.details.submit_review')}
        </Button>
        
        {existingReview?.status === "Pending Validation" && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            {t('service.details.pending_approval')}
          </p>
        )}
      </form>
    </Form>
  );
};

export default ReviewForm;
